import { Mail, Phone, MapPin, Crown, Building, Pencil, Trash2, Check, X, CalendarPlus, Repeat, Clock, CreditCard, FileText, Map, Navigation, ClipboardList, TreeDeciduous, Gift, Users, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { LoyaltySection } from "./index";
import ActiveBookingsSection from "./ActiveBookingsSection";
import DepositHistory from "./DepositHistory";
import { useNavigate } from "react-router-dom";

const CustomerListItem = ({
  customer,
  isEditing,
  editForm,
  setEditForm,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onToggleVip,
  onTogglePro,
  onEditLoyalty,
  onManageShirts,
  onManagePrepaid,
  onManageTreatments,
  onGrantSakuraDraw,
  onRemoveSakuraDraw,
  onFillSakuraGauge,
  onAddPayment,
  onShowHistory,
  onPaymentLinks,
  onCreateRecurring,
  onBookCustomer,
  onSendArticlesReadySms
}) => {
  const navigate = useNavigate();
  
  if (isEditing) {
    return (
      <div className="border rounded-lg p-4 bg-slate-50">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Prénom</Label>
              <Input
                value={editForm.first_name}
                onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Nom</Label>
              <Input
                value={editForm.last_name}
                onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
              />
            </div>
          </div>
          <div>
            <Label>Adresse</Label>
            <Input
              value={editForm.address}
              onChange={(e) => setEditForm({...editForm, address: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Téléphone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input
              value={editForm.notes}
              onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
              placeholder="Notes internes..."
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onCancelEdit}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={onSaveEdit} className="bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Header with name and badges */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-lg">
              {customer.first_name} {customer.last_name}
            </h3>
            {customer.is_pro && (
              <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-2 py-0.5 flex items-center gap-1" title="Client professionnel">
                <Building className="w-3 h-3" />
                PRO
              </Badge>
            )}
            {customer.is_vip && (
              <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-2 py-0.5 flex items-center gap-1" title="Réservation prioritaire">
                <Crown className="w-3 h-3" />
                VIP
              </Badge>
            )}
            {customer.sakura_bonus_draws > 0 && (
              <Badge className="bg-gradient-to-r from-pink-400 to-rose-500 text-white text-xs px-2 py-0.5 flex items-center gap-1" title="Tirages Sakura disponibles">
                <TreeDeciduous className="w-3 h-3" />
                {customer.sakura_bonus_draws} tirage{customer.sakura_bonus_draws > 1 ? 's' : ''}
              </Badge>
            )}
            {customer.referral_code && (
              <Badge className="bg-gradient-to-r from-purple-400 to-indigo-500 text-white text-xs px-2 py-0.5 flex items-center gap-1" title={`Code: ${customer.referral_code}`}>
                <Gift className="w-3 h-3" />
                {customer.referral_count || 0}/5 parrainages
              </Badge>
            )}
            {customer.referred_by && (
              <Badge className="bg-green-100 text-green-700 text-xs px-2 py-0.5 flex items-center gap-1" title={`Parrainé par: ${customer.referred_by}`}>
                <Users className="w-3 h-3" />
                Filleul
              </Badge>
            )}
            <Badge className="bg-slate-100 text-slate-600">
              {customer.booking_count || 0} réservation(s)
            </Badge>
          </div>
          
          {/* Contact info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
            {customer.address && (
              <div className="flex items-center gap-2 col-span-full">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="flex-1">{customer.address}</span>
                <div className="flex gap-1 flex-shrink-0">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    title="Ouvrir dans Google Maps"
                  >
                    <Map className="w-3 h-3" />
                    Maps
                  </a>
                  <a 
                    href={`https://waze.com/ul?q=${encodeURIComponent(customer.address)}&navigate=yes`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-cyan-100 text-cyan-700 rounded hover:bg-cyan-200 transition-colors"
                    title="Ouvrir dans Waze"
                  >
                    <Navigation className="w-3 h-3" />
                    Waze
                  </a>
                </div>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline">
                  {customer.phone}
                </a>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline">
                  {customer.email}
                </a>
              </div>
            )}
            {customer.notes && (
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="italic">{customer.notes}</span>
              </div>
            )}
          </div>
          
          {/* Loyalty Section */}
          <LoyaltySection
            customer={customer}
            onEditLoyalty={onEditLoyalty}
            onManageShirts={onManageShirts}
            onManagePrepaid={onManagePrepaid}
            onManageTreatments={onManageTreatments}
            onGrantSakuraDraw={onGrantSakuraDraw}
            onRemoveSakuraDraw={onRemoveSakuraDraw}
            onFillSakuraGauge={onFillSakuraGauge}
            onAddPayment={onAddPayment}
          />
          
          {/* Active Bookings Section */}
          <ActiveBookingsSection bookings={customer.active_bookings} />
          
          {/* Deposit History Section */}
          <DepositHistory 
            customerId={customer.id} 
            getAuth={() => {
              const auth = localStorage.getItem("adminAuth");
              return auth ? { Authorization: `Basic ${auth}` } : null;
            }}
          />
        </div>
        
        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          <Button 
            onClick={() => onTogglePro(customer)}
            variant={customer.is_pro ? "default" : "outline"}
            size="sm"
            className={customer.is_pro 
              ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white" 
              : "text-blue-600 border-blue-300 hover:bg-blue-50"}
          >
            <Building className="w-4 h-4 mr-1" />
            {customer.is_pro ? "Retirer PRO" : "Donner PRO"}
          </Button>
          <Button 
            onClick={() => onToggleVip(customer)}
            variant={customer.is_vip || customer.is_vip_manual ? "default" : "outline"}
            size="sm"
            className={customer.is_vip || customer.is_vip_manual 
              ? "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-white" 
              : "text-amber-600 border-amber-300 hover:bg-amber-50"}
          >
            <Crown className="w-4 h-4 mr-1" />
            {customer.is_vip_manual ? "Retirer VIP" : "Donner VIP"}
          </Button>
          <Button 
            onClick={() => onBookCustomer(customer)}
            className="bg-primary hover:bg-primary/90"
            size="sm"
          >
            <CalendarPlus className="w-4 h-4 mr-1" />
            Réserver
          </Button>
          <Button 
            onClick={() => navigate(`/admin/deposit/${customer.id}`)}
            variant="outline"
            size="sm"
            className="text-orange-600 border-orange-300 hover:bg-orange-50"
          >
            <ClipboardList className="w-4 h-4 mr-1" />
            Dépôt
          </Button>
          <Button 
            onClick={() => onCreateRecurring(customer)}
            variant="outline"
            size="sm"
            className="text-purple-600 border-purple-300 hover:bg-purple-50"
          >
            <Repeat className="w-4 h-4 mr-1" />
            Récurrent
          </Button>
          <Button 
            variant="outline"
            onClick={() => onShowHistory(customer)}
            size="sm"
          >
            <Clock className="w-4 h-4 mr-1" />
            Historique
          </Button>
          <Button 
            onClick={() => onPaymentLinks(customer)}
            variant="outline"
            size="sm"
            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
          >
            <CreditCard className="w-4 h-4 mr-1" />
            Factures
          </Button>
          {onSendArticlesReadySms && customer.phone && (
            <Button 
              onClick={() => onSendArticlesReadySms(customer)}
              variant="outline"
              size="sm"
              className="text-green-600 border-green-300 hover:bg-green-50"
              title="Envoyer SMS: Articles prêts"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              SMS Prêt
            </Button>
          )}
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onStartEdit(customer)}
              className="text-blue-500 hover:bg-blue-50"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onDelete(customer.id)}
              className="text-red-500 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerListItem;
